import { useState } from 'react';
import type { Lead, LeadStatus, NoteEntry } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const statusOptions: LeadStatus[] = ['New', 'Contacted', 'Quoted', 'Negotiating', 'Won', 'Lost'];

const statusColors: Record<LeadStatus, { bg: string; text: string }> = {
  New: { bg: '#F0FDFA', text: '#0D9488' },
  Contacted: { bg: '#F5F3FF', text: '#7C3AED' },
  Quoted: { bg: '#FFFBEB', text: '#D97706' },
  Negotiating: { bg: '#FFF7ED', text: '#EA580C' },
  Won: { bg: '#ECFDF5', text: '#059669' },
  Lost: { bg: '#FEF2F2', text: '#DC2626' },
};

const noteTypeIcon: Record<NoteEntry['type'], { icon: string; color: string; label: string }> = {
  note: { icon: 'ri-sticky-note-line', color: '#0D9488', label: 'Note' },
  call: { icon: 'ri-phone-line', color: '#7C3AED', label: 'Call' },
  email: { icon: 'ri-mail-line', color: '#D97706', label: 'Email' },
  whatsapp: { icon: 'ri-whatsapp-line', color: '#059669', label: 'WhatsApp' },
  status: { icon: 'ri-arrow-right-circle-line', color: '#6B7280', label: 'Status Change' },
};

interface LeadDetailModalProps {
  lead: Lead;
  onClose: () => void;
  onStatusChange: (leadId: string, status: LeadStatus) => void;
}

export default function LeadDetailModal({ lead, onClose, onStatusChange }: LeadDetailModalProps) {
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [notes, setNotes] = useState<NoteEntry[]>(lead.notes);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<NoteEntry['type']>('note');
  const [activeTab, setActiveTab] = useState<'notes' | 'details'>('notes');

  const handleStatusChange = (newStatus: LeadStatus) => {
    setStatus(newStatus);
    onStatusChange(lead.id, newStatus);
    const statusNote: NoteEntry = {
      id: `sn-${Date.now()}`,
      author: 'You',
      authorAvatar: 'MK',
      text: `Status changed to ${newStatus}`,
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
      type: 'status',
    };
    setNotes((prev) => [statusNote, ...prev]);
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const newNote: NoteEntry = {
      id: `n-${Date.now()}`,
      author: 'You',
      authorAvatar: 'MK',
      text: noteText.trim(),
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
      type: noteType,
    };
    setNotes((prev) => [newNote, ...prev]);
    setNoteText('');
  };

  const sc = statusColors[status];
  const tabs = ['notes', 'details'] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: brand }}
            >
              {lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">{lead.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <i className="ri-phone-line text-xs text-gray-400" />
                <span className="text-xs text-gray-500">{lead.phone}</span>
                <span className="text-gray-300">·</span>
                <i className="ri-mail-line text-xs text-gray-400" />
                <span className="text-xs text-gray-500">{lead.email}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Status Bar */}
        <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          {statusOptions.map((s) => {
            const c = statusColors[s];
            const active = status === s;
            return (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                className="text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-all whitespace-nowrap"
                style={
                  active
                    ? { backgroundColor: c.text, color: 'white' }
                    : { backgroundColor: c.bg, color: c.text }
                }
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Summary Cards */}
        <div className="px-6 py-4 grid grid-cols-3 gap-3 border-b border-gray-100">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-1">Service</p>
            <div className="flex items-center gap-1.5">
              <i className={`${lead.serviceIcon} text-sm`} style={{ color: brand }} />
              <p className="text-xs font-semibold text-gray-700 leading-snug">{lead.service}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-1">Budget</p>
            <p className="text-sm font-bold" style={{ color: brand }}>{lead.budget}</p>
            <p className="text-xs text-gray-400">{lead.pax} pax</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-1">Travel Date</p>
            <p className="text-xs font-semibold text-gray-700">{lead.travelDate}</p>
            <p className="text-xs text-gray-400">{lead.country}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-3 text-sm font-medium capitalize cursor-pointer transition-colors whitespace-nowrap"
              style={
                activeTab === tab
                  ? { color: brand, borderBottom: `2px solid ${brand}` }
                  : { color: '#9CA3AF' }
              }
            >
              {tab === 'notes' ? 'Activity & Notes' : 'Lead Details'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'notes' && (
            <div className="p-6">
              {/* Add Note */}
              <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  {(['note', 'call', 'email', 'whatsapp'] as NoteEntry['type'][]).map((t) => {
                    const info = noteTypeIcon[t];
                    return (
                      <button
                        key={t}
                        onClick={() => setNoteType(t)}
                        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-all"
                        style={
                          noteType === t
                            ? { backgroundColor: info.color, color: 'white' }
                            : { backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB' }
                        }
                      >
                        <i className={`${info.icon} text-xs`} />
                        {info.label}
                      </button>
                    );
                  })}
                </div>
                <textarea
                  rows={3}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={`Add a ${noteType}...`}
                  className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 bg-white placeholder-gray-400"
                  style={{ '--tw-ring-color': brand } as React.CSSProperties}
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleAddNote}
                    disabled={!noteText.trim()}
                    className="text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    style={{ backgroundColor: brand }}
                  >
                    Save Note
                  </button>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                {notes.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">No activity yet. Add a note to get started.</p>
                )}
                {notes.map((note) => {
                  const typeInfo = noteTypeIcon[note.type];
                  return (
                    <div key={note.id} className="flex gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${typeInfo.color}15` }}
                      >
                        <i className={`${typeInfo.icon} text-sm`} style={{ color: typeInfo.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-700">{note.author}</span>
                          <span
                            className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color }}
                          >
                            {typeInfo.label}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto">{note.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{note.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Full Name', value: lead.name, icon: 'ri-user-line' },
                  { label: 'Phone', value: lead.phone, icon: 'ri-phone-line' },
                  { label: 'Email', value: lead.email, icon: 'ri-mail-line' },
                  { label: 'Country', value: lead.country, icon: 'ri-map-pin-line' },
                  { label: 'Source', value: lead.source, icon: 'ri-share-line' },
                  { label: 'Assigned To', value: lead.assignedTo, icon: 'ri-user-settings-line' },
                  { label: 'Travel Date', value: lead.travelDate, icon: 'ri-calendar-line' },
                  { label: 'Created', value: lead.createdAt, icon: 'ri-time-line' },
                ].map((item) => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <i className={`${item.icon} text-xs text-gray-400`} />
                      <p className="text-xs text-gray-400">{item.label}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-700 truncate">{item.value}</p>
                  </div>
                ))}
              </div>

              {lead.tags.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {lead.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs font-medium px-3 py-1 rounded-full"
                        style={{ backgroundColor: `${brand}15`, color: brand }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          <button
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer transition-opacity hover:opacity-90 whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-calendar-add-line" />
            Create Booking
          </button>
          <button className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-whatsapp-line" />
            WhatsApp
          </button>
          <button className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-mail-send-line" />
            Send Quote
          </button>
          <button className="ml-auto flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-delete-bin-line" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
